import { withChildren } from '@builder.io/react';
import { ComponentProps, ReactNode } from 'react';
import { HOW_ZAPIER_WORKS_VIDEO } from '../../../constants/stories';
import { ContentStyle, HeadingStyle } from '../../../global/types';
import { createBuilderModule } from '../../../builder';
import { builderElement } from '../../../builder/element-factories';
import BlockBuilder from '../../Block/Block.builder';
import { backgroundColors } from '../../Section/background';
import { BlockSection } from './BlockSection';
import { COLOR_KEYS } from '../../../constants';

/**
 * Pass Builder's children in as BlockSection.props.media
 *  Note: Favoring withChildren() where possible over custom uiBlocks implementation.
 */
function BlockSectionBuilder({
  children,
  ...otherProps
}: Omit<ComponentProps<typeof BlockSection>, 'media'> & {
  children: ReactNode;
}) {
  return <BlockSection {...otherProps} media={children} />;
}

const inputsToReset = [
  'ctaText',
  'ctaHref',
  'ctaStyle',
  'ctaName',
  'ctaGoal',
  'secondaryCtaText',
  'secondaryCtaHref',
  'secondaryCtaStyle',
  'secondaryCtaName',
  'secondaryCtaGoal',
  'smallText',
  'icon',
];

const sharedBlockInputs = BlockBuilder.config.inputs
  // isPageWidth is not configurable for the BlockSection implementation of Block
  .filter((input) => input.name !== 'isPageWidth')
  .map((input) =>
    inputsToReset.includes(input.name)
      ? { ...input, defaultValue: undefined }
      : input,
  );

export default createBuilderModule(withChildren(BlockSectionBuilder), {
  name: 'BlockSection',
  image: 'https://cdn.zappy.app/96cd6e766d7dcf818b77a403301fe306.png',
  defaultChildren: [
    builderElement('WistiaVideo', {
      wistiaId: HOW_ZAPIER_WORKS_VIDEO,
    }),
  ],
  /**
   * Block and BlockSection have nearly identical inputs
   */
  inputs: [
    {
      name: 'background',
      type: 'string',
      enum: Object.values(backgroundColors),
      defaultValue: COLOR_KEYS.transparent,
      required: true,
    },
    // Section Props, optional section heading(s)
    {
      name: 'sectionHeading',
      type: 'string',
    },
    {
      name: 'sectionSubheading',
      type: 'string',
    },
    ...sharedBlockInputs,
    {
      name: 'headingStyle',
      type: 'string',
      enum: Object.keys(HeadingStyle),
      defaultValue: HeadingStyle.pageHeader5,
    },
    {
      name: 'contentStyle',
      type: 'string',
      enum: Object.keys(ContentStyle),
      defaultValue: ContentStyle.paragraph1,
    },
  ],
});
